/**
 * DEFUSE S3 — TYPED Ellipse adapter (`EllipseEntity` → `RenderableEntity`).
 *
 * Beside-the-legacy counterpart of
 * `apps/web/src/app/scene3d/adapters/ellipseToRenderable.ts`. The legacy
 * adapter reads the `EntityPreview.properties` string bag (`"Center X"`,
 * `"Major axis X"`, `"Ratio"`, `"Start parameter"`, …); this typed adapter
 * reads the canonical `EllipseEntity` model fields directly
 * (`center` / `majorAxisEndpoint` / `ratio` / `startParam` / `endParam`,
 * see `model.ts` `ellipseEntitySchema`).
 *
 * The geometry it builds, the `applyOffset` application, and the bounds
 * computation are IDENTICAL to legacy so the output is byte-for-byte the
 * same `RenderableEntity` for the same entity (S3 golden-parity).
 *
 * Category B: standalone file, NOT imported into any render path yet; the
 * legacy adapter and the live pipeline are untouched.
 *
 * # Parity notes
 *
 *   - `center` — `EllipseEntity.center` is `drawingPointOptionalZSchema`
 *     (`{x, y, z?}`, SL-15). The real center-z (DXF 30) is read via
 *     `optionalPointZ` (a planar ellipse omits z → 0, byte-identical to the
 *     legacy `"Center Z"` default). `applyOffset` is applied to the center
 *     exactly as legacy.
 *   - `majorAxisEndpoint` — a vector RELATIVE to center; the legacy adapter
 *     does NOT offset it, and neither do we. Also `{x, y, z?}` (SL-15), so the
 *     real major-axis z (DXF 31) rides via `optionalPointZ` (planar → 0).
 *     NOTE the ellipse's OCS extrusion/normal is NOT applied to the tessellation
 *     (a systemic conic-render limitation shared with Circle/Arc — the sweep is
 *     built in the XY plane at elevation `center.z`); the model+DXF fidelity is
 *     full, the visible tilt of the ellipse PLANE is a later render slice.
 *   - `ratio` — the schema guarantees `> 0` (`z.number().positive()`). The
 *     legacy `readPositiveNumber(..., "Ratio", 1)` falls back to 1 only for a
 *     non-positive / non-finite value; we mirror that clamp so a degenerate
 *     (non-validated) entity produces the SAME 1 fallback.
 *   - `startParam` / `endParam` — radians, CCW from the major axis. The model
 *     defaults are 0 / 2π (`ellipseEntitySchema`), matching the legacy bag
 *     defaults (`readNumber(..., "Start parameter")` → 0, `"End parameter"`
 *     → `Math.PI * 2`). NO angle-unit conversion: ELLIPSE params are stored
 *     in radians in BOTH the bag and the model (unlike ARC's degrees), so the
 *     typed reader passes them through unchanged — no double-convert.
 */

import type { EllipseEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  optionalPointZ,
  type AdapterContext,
} from "../adapterContext";
import { buildEllipseSceneGeometry } from "../sceneGeometryBuilders";

export function ellipseFromEntity(
  entity: EllipseEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // `center` is `{x, y}` on the schema → a top-level entity reads 0 (legacy
  // `"Center Z"` default — golden parity intact). A block-flatten clone
  // carries a REAL transformed z (@codex #431 round-2 Z sweep): read it via
  // the shared `optionalPointZ` so ellipse children lift with their
  // Line/Circle/Arc siblings. The shared builder applies `applyOffset`.
  const centerRaw: Vec3 = {
    x: entity.center.x,
    y: entity.center.y,
    z: optionalPointZ(entity.center),
  };
  // Major-axis endpoint is a vector relative to center → NOT offset (the shared
  // builder passes it through). SL-15 — read the real major-axis z (DXF 31) via
  // the shared `optionalPointZ` (the model field is now optional-z); a planar
  // ellipse reads 0 (byte-identical to the legacy `"Major axis Z"` default),
  // an out-of-XY-plane major axis carries its real z.
  const majorAxis: Vec3 = {
    x: entity.majorAxisEndpoint.x,
    y: entity.majorAxisEndpoint.y,
    z: optionalPointZ(entity.majorAxisEndpoint),
  };
  // Schema guarantees ratio > 0; mirror the legacy `readPositiveNumber(...,1)`
  // clamp so a degenerate (non-positive / non-finite) value still falls back
  // to 1 — byte-identical to the bag path for any input.
  const ratio =
    Number.isFinite(entity.ratio) && entity.ratio > 0 ? entity.ratio : 1;

  // SL-15 F-C — carry the OCS extrusion normal (DXF 210/220/230) so a tilted
  // ellipse renders in its true plane. `EllipseEntity.extrusion` is a full
  // `{x, y, z}` (schema default (0,0,1)); the sampler treats (0,0,1) as the
  // planar default, so a planar ellipse is byte-identical to the 2D render.
  //
  // SL-15 round-9 @codex F1 (P1) — `extrusion` is a REQUIRED schema default
  // (0,0,1), but a reducer that constructs an Ellipse WITHOUT running Zod hands in
  // an entity whose `extrusion` is `undefined` at runtime: the non-uniform
  // Circle/Arc → Ellipse promotion in block explode (`blockReducerHelpers`,
  // `blockMutations`) builds the ellipse via `as DrawingEntity` casts with no
  // extrusion, and `assembleDrawingDocument` does not re-apply the Zod default.
  // Reading `.x/.y/.z` off that `undefined` throws → the whole layer fails to
  // render (blank canvas in production). Default a missing/undefined normal to the
  // (0,0,1) WCS plane at the point of consumption: a promotion-built planar
  // ellipse then renders as a valid planar ellipse (z ≡ center.z), while a present
  // non-default normal still tilts the plane.
  const sourceExtrusion = entity.extrusion ?? { x: 0, y: 0, z: 1 };
  const extrusion: Vec3 = {
    x: sourceExtrusion.x,
    y: sourceExtrusion.y,
    z: sourceExtrusion.z,
  };

  const { geometry, bounds } = buildEllipseSceneGeometry(
    centerRaw,
    majorAxis,
    ratio,
    entity.startParam,
    entity.endParam,
    extrusion,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
